import { useId, useMemo, useState, type ComponentType, type ReactNode } from 'react';
import type { LucideProps } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import { cn } from '@/lib/cn';

export type SidebarTab = {
  key: string;
  label: string;
  icon?: ComponentType<LucideProps>;
  /** Optional count rendered as a pill on the right of the row. Falsy values are hidden. */
  count?: number | null;
  /** Tabs sharing the same group string render under one section header. Tabs without a group sit at the top, unlabeled. */
  group?: string;
  content: ReactNode;
};

type Props = {
  tabs: SidebarTab[];
  initialKey?: string;
  paramName?: string;
  className?: string;
};

type Section = { label?: string; tabs: SidebarTab[] };

function partition(tabs: SidebarTab[]): Section[] {
  const sections: Section[] = [];
  const indexByLabel = new Map<string | undefined, number>();
  for (const tab of tabs) {
    const label = tab.group;
    const existing = indexByLabel.get(label);
    if (existing != null) {
      sections[existing].tabs.push(tab);
    } else {
      indexByLabel.set(label, sections.length);
      sections.push({ label, tabs: [tab] });
    }
  }
  return sections;
}

export function SidebarTabs({ tabs, initialKey, paramName, className }: Props) {
  const groupId = useId();
  const [searchParams, setSearchParams] = useSearchParams();
  const validKeys = useMemo(() => new Set(tabs.map((t) => t.key)), [tabs]);
  const fallback = initialKey && validKeys.has(initialKey) ? initialKey : tabs[0]?.key;

  const [localActive, setLocalActive] = useState(fallback);
  const urlActive = paramName ? searchParams.get(paramName) : null;
  const active = paramName
    ? urlActive && validKeys.has(urlActive)
      ? urlActive
      : fallback
    : localActive;

  function setActive(key: string) {
    if (paramName) {
      const next = new URLSearchParams(searchParams);
      next.set(paramName, key);
      setSearchParams(next, { replace: true });
    } else {
      setLocalActive(key);
    }
  }

  const sections = useMemo(() => partition(tabs), [tabs]);

  return (
    <div className={cn('flex items-start gap-6', className)}>
      <nav
        role="tablist"
        aria-orientation="vertical"
        className="sticky top-4 w-52 shrink-0 self-start"
      >
        {sections.map((section, i) => (
          <div key={section.label ?? `__${i}`} className={i === 0 ? '' : 'mt-5'}>
            {section.label ? (
              <div className="mb-1 px-2 text-[10px] font-semibold uppercase tracking-wider text-[var(--color-foreground-subtle)]">
                {section.label}
              </div>
            ) : null}
            <ul className="space-y-0.5">
              {section.tabs.map((t) => {
                const isActive = t.key === active;
                const Icon = t.icon;
                return (
                  <li key={t.key}>
                    <button
                      type="button"
                      role="tab"
                      aria-selected={isActive}
                      aria-controls={`${groupId}-${t.key}`}
                      onClick={() => setActive(t.key)}
                      className={cn(
                        'group/row relative flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm transition-colors',
                        isActive
                          ? 'bg-[var(--color-muted)] font-medium text-[var(--color-foreground)]'
                          : 'text-[var(--color-foreground-muted)] hover:bg-[var(--color-muted)]/60 hover:text-[var(--color-foreground)]',
                      )}
                    >
                      <span
                        aria-hidden
                        className={cn(
                          'absolute left-0 top-1/2 h-5 w-0.5 -translate-y-1/2 rounded-r-sm transition-colors',
                          isActive ? 'bg-[var(--color-primary)]' : 'bg-transparent',
                        )}
                      />
                      {Icon ? (
                        <Icon
                          className={cn(
                            'size-4 shrink-0',
                            isActive
                              ? 'text-[var(--color-foreground)]'
                              : 'text-[var(--color-foreground-subtle)] group-hover/row:text-[var(--color-foreground-muted)]',
                          )}
                        />
                      ) : null}
                      <span className="flex-1 truncate">{t.label}</span>
                      {t.count ? (
                        <span
                          className={cn(
                            'inline-flex min-w-[1.25rem] items-center justify-center rounded-full px-1.5 text-[10px] font-medium tabular-nums',
                            isActive
                              ? 'bg-[var(--color-primary)]/15 text-[var(--color-primary)]'
                              : 'bg-[var(--color-muted)] text-[var(--color-foreground-muted)]',
                          )}
                        >
                          {t.count}
                        </span>
                      ) : null}
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </nav>

      <div className="min-w-0 flex-1">
        {tabs.map((t) => (
          <div
            key={t.key}
            id={`${groupId}-${t.key}`}
            role="tabpanel"
            hidden={t.key !== active}
          >
            {t.key === active ? t.content : null}
          </div>
        ))}
      </div>
    </div>
  );
}
